import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setStatus({
        type: 'error',
        message: 'Kein Reset-Token gefunden. Bitte fordere einen neuen Reset-Link an.'
      });
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const validatePassword = () => {
    if (password.length < 8) {
      setStatus({
        type: 'error',
        message: 'Das Passwort muss mindestens 8 Zeichen lang sein.'
      });
      return false;
    }

    if (password !== confirmPassword) {
      setStatus({
        type: 'error',
        message: 'Die Passwörter stimmen nicht überein.'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Passwort erfolgreich zurückgesetzt! Du wirst in 3 Sekunden zur Anmeldeseite weitergeleitet...'
        });
        
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setStatus({
          type: 'error',
          message: data.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Verbindungsfehler. Bitte überprüfe deine Internetverbindung.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { text: '', color: '', width: '0%' };
    if (password.length < 6) return { text: 'Schwach', color: '#e74c3c', width: '25%' };
    if (password.length < 10) return { text: 'Mittel', color: '#f39c12', width: '50%' };
    if (password.length < 12) return { text: 'Gut', color: '#3498db', width: '75%' };
    return { text: 'Stark', color: '#2ecc71', width: '100%' };
  };

  const strength = getPasswordStrength(password);

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <h1>🔑 Neues Passwort setzen</h1>
            <p>Bitte wähle ein sicheres neues Passwort für dein PedalScot Konto.</p>
          </div>

          {status.message && (
            <div className={`status-message ${status.type}`}>
              {status.type === 'success' && <span className="status-icon">✅</span>}
              {status.type === 'error' && <span className="status-icon">❌</span>}
              {status.message}
            </div>
          )}

          {token && !status.type === 'success' && (
            <form onSubmit={handleSubmit} className="reset-password-form">
              <div className="form-group">
                <label htmlFor="password">Neues Passwort</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  required
                  disabled={isSubmitting}
                />
                {password && (
                  <div className="password-strength">
                    <div className="strength-bar">
                      <div 
                        className="strength-fill"
                        style={{ 
                          width: strength.width,
                          backgroundColor: strength.color
                        }}
                      ></div>
                    </div>
                    <span className="strength-text" style={{ color: strength.color }}>
                      {strength.text}
                    </span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Passwort bestätigen</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort erneut eingeben"
                  required
                  disabled={isSubmitting}
                />
                {confirmPassword && password === confirmPassword && (
                  <div className="match-indicator success">
                    ✓ Passwörter stimmen überein
                  </div>
                )}
                {confirmPassword && password !== confirmPassword && (
                  <div className="match-indicator error">
                    ✗ Passwörter stimmen nicht überein
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting || !token}
              >
                {isSubmitting ? 'Setze zurück...' : 'Passwort zurücksetzen'}
              </button>
            </form>
          )}

          <div className="security-tips">
            <h3>🛡️ Tipps für ein sicheres Passwort:</h3>
            <ul>
              <li>Verwende mindestens 8 Zeichen</li>
              <li>Kombiniere Groß- und Kleinbuchstaben</li>
              <li>Füge Zahlen und Sonderzeichen hinzu</li>
              <li>Verwende kein offensichtliches Passwort</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
